import {
  MutationCache,
  QueryCache,
  QueryClient,
  type DefaultOptions,
  type Query,
} from '@tanstack/react-query';
import { isPermissionRefused } from '@citibox-panel/api-client';
import {
  generationOfMutation,
  generationOfQuery,
  handlePermissionRefusal,
  rememberMutationView,
  rememberQueryView,
  viewOfMutation,
  viewOfQuery,
} from './permissionRefusalHandler';

// One retry for a transient failure, none for a Rechazo por permiso: asking again cannot change the
// server's answer, it only delays the screen that explains it.
export function retryUnlessRefused(failureCount: number, error: unknown): boolean {
  return !isPermissionRefused(error) && failureCount < 1;
}

// A factory so tests build the same client production runs, handlers included.
export function createQueryClient(queries: DefaultOptions['queries'] = {}): QueryClient {
  // Reads and writes alike: wherever a Rechazo por permiso lands, the same handler answers it, for
  // the view and the session that made the request.
  const client: QueryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        handlePermissionRefusal(client, error, {
          view: viewOfQuery(query),
          generation: generationOfQuery(query),
        });
      },
    }),
    mutationCache: new MutationCache({
      onMutate: (_variables, mutation) => {
        rememberMutationView(mutation);
      },
      onError: (error, _variables, _context, mutation) => {
        handlePermissionRefusal(client, error, {
          view: viewOfMutation(mutation),
          generation: generationOfMutation(mutation),
          mutation,
        });
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: retryUnlessRefused,
        refetchOnWindowFocus: false,
        ...queries,
      },
    },
  });
  // Bind every read to the view on screen when its fetch starts, observed or an imperative
  // fetchQuery. The listener runs synchronously inside the fetch dispatch, before the queryFn can
  // settle, so the binding is in place before any error reaches the handler.
  client.getQueryCache().subscribe((event) => {
    // The cache's event types its query with `any` generics; the binding reads only its identity.
    if (event.type === 'updated' && event.action.type === 'fetch') {
      rememberQueryView(event.query as Query<unknown, unknown>);
    }
  });
  return client;
}

export const queryClient = createQueryClient();
